// src/components/Gantt/useGanttDrag.js
// Перетаскивание полосы задачи: сдвиг дат и растягивание краёв.
//
// Во время перетаскивания не трогаем React-state: правим CSS-переменные
// прямо на DOM-элементе через ref. Иначе каждый mousemove дёргал бы
// ре-рендер всего дерева (сотни строк).
//
// Границы delta (в днях) приходят снаружи через getBounds(task, mode):
//   { min, max } — допустимый диапазон deltaDays для конкретного режима;
//   null/undefined — без ограничений.
// Клипование в handleMove — чтобы визуально полоса не уезжала за предел:
// иначе пользователь тянет, а на mouseup полоса «щёлкает» назад.
import { useCallback, useRef } from "react";

const EDGE_PX = 6;

export function useGanttDrag({ enabled, DW, onCommit, getBounds }) {
  const dragRef = useRef(null);

  const onBarMouseDown = useCallback(
    (e, task, geo, mode) => {
      if (!enabled) return;
      if (task.dependencyId) return; // даты зафиксированы предшественником
      e.preventDefault();
      e.stopPropagation();

      const barEl = e.currentTarget;
      const origLeft = barEl.style.getPropertyValue("--bar-left");
      const origWidth = barEl.style.getPropertyValue("--bar-width");
      const startX = e.clientX;
      let deltaDays = 0;
      dragRef.current = task.id;
      barEl.classList.add("gantt-dragging");

      const bounds = getBounds ? getBounds(task, mode) : null;
      const minDelta = bounds?.min ?? -Infinity;
      const maxDelta = bounds?.max ?? Infinity;

      const handleMove = (ev) => {
        let next = Math.round((ev.clientX - startX) / DW);
        if (next < minDelta) next = minDelta;
        if (next > maxDelta) next = maxDelta;
        if (next === deltaDays) return;
        deltaDays = next;
        const dx = deltaDays * DW;
        if (mode === "move" || mode === "resize-left") {
          barEl.style.setProperty("--bar-left", `calc(${origLeft} + ${dx}px)`);
        }
        if (mode === "resize-left" || mode === "resize-right") {
          const sign = mode === "resize-left" ? -1 : 1;
          barEl.style.setProperty(
            "--bar-width",
            `calc(${origWidth} + ${sign * dx}px)`,
          );
        }
      };

      const reset = () => {
        barEl.style.setProperty("--bar-left", origLeft);
        barEl.style.setProperty("--bar-width", origWidth);
        barEl.classList.remove("gantt-dragging");
        dragRef.current = null;
      };

      const handleUp = () => {
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleUp);
        document.removeEventListener("keydown", handleKey);

        const wasDragged = deltaDays !== 0;

        if (wasDragged) {
          const swallowClick = (event) => {
            event.stopPropagation();
            event.preventDefault();
            document.removeEventListener("click", swallowClick, true);
          };
          document.addEventListener("click", swallowClick, true);

          setTimeout(() => {
            document.removeEventListener("click", swallowClick, true);
          }, 100);

          // Не вызываем reset(), чтобы полоска не отскакивала назад
          onCommit(task, mode, deltaDays);
        } else {
          reset();
        }
      };

      const handleKey = (ev) => {
        if (ev.key !== "Escape") return;
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleUp);
        document.removeEventListener("keydown", handleKey);
        reset();
      };

      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleUp);
      document.addEventListener("keydown", handleKey);
    },
    [enabled, DW, onCommit, getBounds],
  );

  const detectMode = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x < EDGE_PX) return "resize-left";
    if (x > rect.width - EDGE_PX) return "resize-right";
    return "move";
  }, []);

  return { onBarMouseDown, detectMode };
}
